// Scenario return, not measured AI-attributed revenue or all-in profit.
export function estimatedRoi(metric, billing, period, assumptions, gymId) {
  const unavailable = reason => ({status:'no_data',percent:null,reason});
  if(metric?.status!=='measured'||!Number.isSafeInteger(metric.value)||metric.value<0)return unavailable('A verified appointment count is needed.');
  if(!validNumber(assumptions?.ltv,1000000)||!validNumber(assumptions?.rate,100))return unavailable('Enter a lifetime value and join rate.');
  const ltv=Number(assumptions?.ltv),rate=Number(assumptions?.rate);
  if(!Number.isFinite(ltv)||ltv<0||ltv>1000000||!Number.isFinite(rate)||rate<0||rate>100)return unavailable('Enter a valid lifetime value and join rate.');
  const start=Date.parse(period?.start),end=Date.parse(period?.end);
  if(!Number.isFinite(start)||!Number.isFinite(end)||start>end)return unavailable('A verified activity period is needed.');
  // Explicit owner-approved scenarios, never fabricated invoices or paid history.
  const monthlyCost=assumedMonthlyCost(gymId);
  if(monthlyCost!==null){
    const periodDays=(end-start)/86400000,periodMonths=periodDays/(365.25/12);
    const cost=monthlyCost*periodMonths;
    if(!Number.isFinite(cost)||cost<=0)return unavailable('A positive activity period is needed for the assumed cost.');
    const modeledCustomers=metric.value*rate/100,modeledValue=modeledCustomers*ltv;
    const percent=(modeledValue-cost)/cost*100;
    if(!Number.isFinite(percent))return unavailable('The estimate could not be calculated.');
    return {status:'estimated',percent,modeledCustomers,modeledValue,costBasis:'assumed_monthly',monthlyCost,periodDays,periodMonths,cost};
  }
  if(billing?.invoices_status!=='measured'||!Array.isArray(billing.invoices)||billing.more_invoices!==false)return unavailable('Complete membership invoice history is needed for this estimate.');
  const paid=billing.invoices.filter(i=>i.status==='paid');
  if(paid.some(i=>!i.paid_at||!Number.isFinite(Date.parse(i.paid_at))))return unavailable('Payment dates could not be verified.');
  const matched=paid.filter(i=>Date.parse(i.paid_at)>=start&&Date.parse(i.paid_at)<=end);
  if(!matched.length)return unavailable('No paid membership invoices were verified in this activity period.');
  if(matched.some(i=>i.currency!=='usd'||!Number.isSafeInteger(i.amount_paid_cents)||i.amount_paid_cents<0)||new Set(matched.map(i=>i.id)).size!==matched.length||matched.some(i=>!i.id))return unavailable('Membership payment evidence could not be verified.');
  const cents=matched.reduce((sum,i)=>sum+i.amount_paid_cents,0);
  if(!Number.isSafeInteger(cents)||cents<=0)return unavailable('A positive membership payment basis is needed to calculate a percentage.');
  const modeledCustomers=metric.value*rate/100,modeledValue=modeledCustomers*ltv;
  const percent=(modeledValue-cents/100)/(cents/100)*100;
  if(!Number.isFinite(percent))return unavailable('The estimate could not be calculated.');
  return {status:'estimated',percent,modeledCustomers,modeledValue,invoiceCount:matched.length,costBasis:'verified_payments',cost:cents/100};
}
const assumedMonthlyCosts=Object.freeze({BBXzDSPkjY83K4tTUvTw:600,dHR1AYqHaip8Ol3CmeRb:500,Icjz8Y08oYjNd52zZCTP:500});
export const assumedMonthlyCost=gymId=>Object.hasOwn(assumedMonthlyCosts,gymId)?assumedMonthlyCosts[gymId]:null;
export const formatRoi = value => new Intl.NumberFormat('en-US',{maximumFractionDigits:1,signDisplay:'exceptZero'}).format(value)+'%';

const clubValues = Object.freeze({
  ubEm8r4gdjsBZx8eWF1e: 1000,
  BBXzDSPkjY83K4tTUvTw: 1500,
  JQxMWHF6N7lV1WK9uwZ8: 2400,
  dHR1AYqHaip8Ol3CmeRb: 4800,
  Icjz8Y08oYjNd52zZCTP: 1200,
});
export function defaultAssumptions(gymId) {
  return {ltv:clubValues[gymId]??'',booking:45,show:65,close:70,basis:'appointments'};
}
export function validNumber(value,max) {
  return (typeof value==='number'||typeof value==='string'&&value.trim()!=='')&&Number.isFinite(Number(value))&&Number(value)>=0&&Number(value)<=max;
}
export function validAssumptions(values) {
  return values && ['appointments','engaged'].includes(values.basis)&&validNumber(values.ltv,1000000)&&['booking','show','close'].every(k=>validNumber(values[k],100));
}
export function loadAssumptions(storage,gymId) {
  const defaults=defaultAssumptions(gymId);
  try {const saved=JSON.parse(storage.getItem(`aigo-funnel-assumptions-v4:${gymId}`)||storage.getItem(`aigo-funnel-assumptions-v3:${gymId}`));if(validAssumptions(saved))return saved;}catch{}
  return defaults;
}
export function funnelEstimate(metrics,values) {
  const unavailable=reason=>({status:'no_data',reason});
  if(!validAssumptions(values))return unavailable('Enter a customer value and percentages from 0 to 100.');
  const metric=values.basis==='engaged'?metrics?.people_engaged:metrics?.people_with_appointments;
  if(metric?.status!=='measured'||!Number.isSafeInteger(metric.value)||metric.value<0)return unavailable('The selected activity count is not verified.');
  const rate=(values.basis==='engaged'?Number(values.booking)/100:1)*Number(values.show)/100*Number(values.close)/100;
  const bookings=metric.value*(values.basis==='engaged'?Number(values.booking)/100:1);
  const shows=bookings*Number(values.show)/100,customers=shows*Number(values.close)/100;
  return {status:'estimated',metric,rate:rate*100,bookings,shows,customers,value:customers*Number(values.ltv)};
}
